import React, {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {authApi, usuariosApi} from '../../../api/api';
import {ApiError} from '../../../api/http';
import type {RespuestaUsuario, RolUsuario, SolicitudCrearUsuario, UsuarioDTO} from '../../../api/Objetos';
import {AdminPageHeader} from '../ui/AdminPageHeader';
import {AdminContentCard} from '../ui/AdminContentCard';
import '../ui/adminUi.css';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../../ui/notifications';
import {useAuth} from '../../../app/providers/AuthProvider';

export function AdminUsersPage() {
    const { t } = useTranslation();
    const { showSuccess, showError } = useNotifications();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const [nombreUsuario, setNombreUsuario] = useState('');
    const [password, setPassword] = useState('');
    const [rol, setRol] = useState<RolUsuario>('LECTOR');
    const [resetUserId, setResetUserId] = useState<number | null>(null);
    const [newPassword, setNewPassword] = useState('');

    const usuariosQ = useQuery<UsuarioDTO[]>({
        queryKey: ['admin', 'usuarios'],
        queryFn: usuariosApi.listar,
    });

    const crearUsuario = useMutation<RespuestaUsuario, Error, SolicitudCrearUsuario>({
        mutationFn: (data) => authApi.crearUsuario(data),
        onSuccess: (data) => {
            setNombreUsuario('');
            setPassword('');
            setRol('LECTOR');
            void queryClient.invalidateQueries({queryKey: ['admin', 'usuarios']});
            showSuccess(t('adminUsersPage.userCreated'), `${data.nombreUsuario} · ${data.rol}`);
        },
        onError: (error) => {
            showError(t('adminUsersPage.errorCreating'), renderCreateUserError(error, t));
        }
    });

    const restablecerPassword = useMutation<void, Error, {id: number; password: string}>({
        mutationFn: ({id, password: nextPassword}) => usuariosApi.restablecerPassword(id, nextPassword),
        onSuccess: () => {
            setResetUserId(null);
            setNewPassword('');
            showSuccess(t('adminUsersPage.passwordResetSuccess'));
        },
        onError: error => showError(t('adminUsersPage.passwordResetError'), renderApiError(error)),
    });

    const eliminarUsuario = useMutation<void, Error, number>({
        mutationFn: usuariosApi.eliminar,
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: ['admin', 'usuarios']});
            showSuccess(t('adminUsersPage.userDeleted'));
        },
        onError: error => showError(t('adminUsersPage.deleteError'), renderApiError(error)),
    });

    const users = [...(usuariosQ.data ?? [])].sort((a, b) => a.nombreUsuario.localeCompare(b.nombreUsuario));

    return (
        <div className="adminPageWrap">
            <AdminPageHeader
                eyebrow={t('adminUsersPage.administration')}
                title={t('adminUsersPage.users')}
                description={t('adminUsersPage.managementDescription')}
            />

            <div className="adminSplitGrid">
                <AdminContentCard
                    title={t('adminUsersPage.createUser')}
                    description={t('adminUsersPage.defineUser')}
                >
                    <form
                        className="stack"
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!nombreUsuario.trim() || !password.trim()) {
                                showError(t('adminUsersPage.errorCreating'), 'Todos los campos son obligatorios');
                                return;
                            }
                            crearUsuario.mutate({nombreUsuario, password, rol});
                        }}
                    >
                        <label className="label">
                            {t('adminUsersPage.username')}
                            <input
                                className="input"
                                value={nombreUsuario}
                                onChange={(e) => setNombreUsuario(e.target.value)}
                            />
                        </label>

                        <label className="label">
                            {t('adminUsersPage.password')}
                            <input
                                className="input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </label>

                        <label className="label">
                            {t('adminUsersPage.role')}
                            <select className="input" value={rol}
                                    onChange={(e) => setRol(e.target.value as RolUsuario)}>
                                <option value="LECTOR">{t('adminUsersPage.readerRole')}</option>
                                <option value="ADMIN">{t('adminUsersPage.adminRole')}</option>
                            </select>
                        </label>

                        <div className="formActions">
                            <button className="btn btnPrimary" type="submit" disabled={crearUsuario.isPending}>
                                {crearUsuario.isPending ? t('adminUsersPage.creating') : t('adminUsersPage.createUserButton')}
                            </button>
                        </div>
                    </form>
                </AdminContentCard>

                <AdminContentCard
                    title={t('adminUsersPage.usageNotes')}
                    description={t('adminUsersPage.accessManagement')}
                >
                    <div className="adminInfoList">
                        <div className="adminInfoItem">
                            <span className="adminInfoItemLabel">{t('adminUsersPage.adminRole')}</span>
                            <div className="adminInfoItemValue">{t('adminUsersPage.adminRoleDescription')}</div>
                        </div>

                        <div className="adminInfoItem">
                            <span className="adminInfoItemLabel">{t('adminUsersPage.readerRole')}</span>
                            <div className="adminInfoItemValue">{t('adminUsersPage.readerRoleDescription')}</div>
                        </div>

                        <div className="adminInfoItem">
                            <span className="adminInfoItemLabel">{t('adminUsersPage.validation')}</span>
                            <div className="adminInfoItemValue">
                                {t('adminUsersPage.validationDescription')}
                            </div>
                        </div>
                    </div>
                </AdminContentCard>
            </div>

            <AdminContentCard
                title={t('adminUsersPage.existingUsers')}
                description={t('adminUsersPage.existingUsersDescription')}
                actions={
                    <button className="btn btnSecondary btnSmall" type="button" onClick={() => void usuariosQ.refetch()}>
                        {t('adminUsersPage.refresh')}
                    </button>
                }
            >
                {usuariosQ.isLoading ? <div className="adminEmptyState">{t('adminUsersPage.loading')}</div> : null}
                {usuariosQ.isError ? (
                    <div className="alert alertError">{renderApiError(usuariosQ.error)}</div>
                ) : null}
                {!usuariosQ.isLoading && !usuariosQ.isError && users.length === 0 ? (
                    <div className="adminEmptyState">{t('adminUsersPage.noUsers')}</div>
                ) : null}

                {users.length > 0 ? (
                    <div className="adminTableWrap adminUsersTableWrap">
                        <table className="adminTable adminUsersTable">
                            <thead>
                                <tr>
                                    <th>{t('adminUsersPage.username')}</th>
                                    <th>{t('adminUsersPage.role')}</th>
                                    <th>{t('adminUsersPage.status')}</th>
                                    <th>{t('adminUsersPage.createdAt')}</th>
                                    <th>{t('adminUsersPage.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => {
                                    const isCurrentUser = user.id === auth.usuario?.id;
                                    return (
                                        <React.Fragment key={user.id}>
                                            <tr>
                                                <td>
                                                    <strong>{user.nombreUsuario}</strong>
                                                    {isCurrentUser ? <span className="adminCurrentUser">{t('adminUsersPage.currentUser')}</span> : null}
                                                </td>
                                                <td><span className="adminBadge isBlue">{t(user.rol === 'ADMIN' ? 'adminUsersPage.adminRole' : 'adminUsersPage.readerRole')}</span></td>
                                                <td>
                                                    <span className={`adminBadge ${user.activo ? 'isGreen' : 'isGray'}`}>
                                                        {user.activo ? t('adminUsersPage.active') : t('adminUsersPage.inactive')}
                                                    </span>
                                                </td>
                                                <td>{formatDate(user.fechaCreacion)}</td>
                                                <td>
                                                    <div className="adminTableActions">
                                                        <button
                                                            className="btn btnSecondary btnSmall"
                                                            type="button"
                                                            onClick={() => {
                                                                setResetUserId(user.id);
                                                                setNewPassword('');
                                                            }}
                                                        >
                                                            {t('adminUsersPage.resetPassword')}
                                                        </button>
                                                        <button
                                                            className="btn btnDanger btnSmall"
                                                            type="button"
                                                            disabled={isCurrentUser || eliminarUsuario.isPending}
                                                            title={isCurrentUser ? t('adminUsersPage.cannotDeleteSelf') : undefined}
                                                            onClick={() => {
                                                                if (window.confirm(t('adminUsersPage.confirmDelete', {user: user.nombreUsuario}))) {
                                                                    eliminarUsuario.mutate(user.id);
                                                                }
                                                            }}
                                                        >
                                                            {t('adminUsersPage.delete')}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {resetUserId === user.id ? (
                                                <tr className="adminPasswordResetRow">
                                                    <td colSpan={5}>
                                                        <form
                                                            className="adminPasswordResetForm"
                                                            onSubmit={event => {
                                                                event.preventDefault();
                                                                if (newPassword.length < 8) {
                                                                    showError(t('adminUsersPage.passwordResetError'), t('adminUsersPage.passwordMinLength'));
                                                                    return;
                                                                }
                                                                restablecerPassword.mutate({id: user.id, password: newPassword});
                                                            }}
                                                        >
                                                            <label className="label">
                                                                {t('adminUsersPage.newPasswordFor', {user: user.nombreUsuario})}
                                                                <input
                                                                    className="input"
                                                                    type="password"
                                                                    autoComplete="new-password"
                                                                    value={newPassword}
                                                                    onChange={event => setNewPassword(event.target.value)}
                                                                />
                                                            </label>
                                                            <div className="adminTableActions">
                                                                <button className="btn btnPrimary btnSmall" type="submit" disabled={restablecerPassword.isPending}>
                                                                    {t('adminUsersPage.savePassword')}
                                                                </button>
                                                                <button className="btn btnGhost btnSmall" type="button" onClick={() => setResetUserId(null)}>
                                                                    {t('adminUsersPage.cancel')}
                                                                </button>
                                                            </div>
                                                        </form>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </AdminContentCard>
        </div>
    );
}

function formatDate(value?: string) {
    if (!value) return '-';
    return new Intl.DateTimeFormat('es-ES', {dateStyle: 'medium'}).format(new Date(value));
}

function renderApiError(error: unknown) {
    if (error instanceof ApiError) return error.message.replace(/^\[\d+\]\s*/, '');
    if (error instanceof Error) return error.message;
    return 'Error inesperado';
}

function renderCreateUserError(error: unknown, t: (key: string) => string) {
    let message = '';
    
    if (error instanceof ApiError) {
        if (error.status === 409 || (error.status === 500 && error.message.toLowerCase().includes('usuario ya existe'))) {
            return t('adminUsersPage.userExists');
        }
        if (error.status === 403) {
            return t('adminUsersPage.permissionError');
        }
        message = error.message;
    } else if (error instanceof Error) {
        message = error.message;
    } else {
        return t('adminUsersPage.unknownError');
    }

    // Remove any error codes like "[400] " from the beginning of the message
    const cleanedMessage = message.replace(/^\[\d+\]\s*/, '');

    if (error instanceof ApiError && error.status === 400) {
        const errorsList = cleanedMessage.split(',').map(msg => {
            let text = msg.trim();
            // Remove field prefixes like "password: " or "nombreUsuario: "
            text = text.replace(/^[a-zA-Z0-9_]+:\s*/, '');
            if (!text) return '';
            return text.charAt(0).toUpperCase() + text.slice(1);
        }).filter(Boolean);
        
        // Remove duplicates and join with newlines
        return Array.from(new Set(errorsList)).join('\n');
    }

    return cleanedMessage;
}
